import React, { useMemo } from 'react'
import { useQuery } from '@apollo/client'

import { SelectWithArrow } from '@src/components/ServicePopover/SelectWithArrow'
import { allOutletDiscountsQuery } from '@src/hooks/sharedQueries/useAllDiscounts/queries/__generated__/getAllDiscounts.graphql-interface'
import { useMarketplace } from '@src/hooks/useMarketplace'
import { convertToSlug } from '@src/utils/convertToSlug'

import {
  StackedMenuContainer,
  MenuTitle,
  MenuSubtitle,
  TitleAndSubtitleContainer,
  StyledLink,
  StackedMenuOuterContainer,
  ChevronContainer,
} from './OutletStackedMenu.styles'

import { SingleDiscount } from '../OutletDiscounts/SingleDiscount'
import { ScrollToContainer } from '../OutletMenu/MenuGroup/MenuGroup.styles'
import {
  DealsAndOffersContainer,
  SingleDiscountContainer,
} from '../OutletMenu/OutletMenuStyles'
import { GetOutletParentMenuIdsDocument } from '../OutletMenu/queries/__generated__/GetParentMenuIds.graphql-interface'
import { MenuItemSearchResults } from '../OutletMenu/MenuItemSearchResults/MenuItemSearchResults'
import { useOutletPageContext } from '../OutletPageContext'

export const OutletStackedMenu: React.FC<{
  allDiscounts: allOutletDiscountsQuery | undefined
}> = ({ allDiscounts }) => {
  const { urlPath } = useMarketplace()
  const {
    showMenuSearchResults,
    outletFulfilment: {
      outlet,
      data: { currentFulfilment },
    },
  } = useOutletPageContext()

  const { data: parentMenuGroupIds, previousData } = useQuery(
    GetOutletParentMenuIdsDocument,
    {
      variables: {
        outletId: outlet.id,
        endOfPrep: currentFulfilment.endOfPrep?.toISOString(),
        narrowFulfilmentMethods: currentFulfilment.narrowType,
      },
    }
  )

  const menuItemGroups = (parentMenuGroupIds || previousData)
    ?.menuItemGroupsForOutlet

  const menuItemsWithoutAddonItems = useMemo(
    () =>
      menuItemGroups
        ? menuItemGroups.filter(({ addOnItemsMenu }) => !addOnItemsMenu)
        : [],
    [menuItemGroups]
  )
  const parentMenuItemGroups = menuItemsWithoutAddonItems.filter(
    menuItemGroup => !menuItemGroup.parentMenu
  )

  if (showMenuSearchResults)
    return <MenuItemSearchResults outletId={outlet.id} />

  return (
    <>
      {allDiscounts?.allDiscountsByOutletId.length ? (
        <>
          <ScrollToContainer id="discountMenuItem" aria-hidden />
          <DealsAndOffersContainer>
            <SingleDiscountContainer>
              {allDiscounts.allDiscountsByOutletId.map(discount => (
                <div key={discount.id}>
                  <SingleDiscount allDiscounts={discount} />
                </div>
              ))}
            </SingleDiscountContainer>
          </DealsAndOffersContainer>
        </>
      ) : null}
      <StackedMenuOuterContainer>
        {parentMenuItemGroups.map(menu => (
          <StyledLink
            key={menu.id}
            to={`/${urlPath}/${outlet.id}/${convertToSlug(
              outlet.displayName
            )}/menu/${menu.id}`}
          >
            <StackedMenuContainer>
              <TitleAndSubtitleContainer>
                <MenuTitle>{menu.name}</MenuTitle>
                {menu.description && (
                  <MenuSubtitle>{menu.description}</MenuSubtitle>
                )}
              </TitleAndSubtitleContainer>
              <ChevronContainer>
                <SelectWithArrow justArrow id="caret" dataPresent={false} />
              </ChevronContainer>
            </StackedMenuContainer>
          </StyledLink>
        ))}
      </StackedMenuOuterContainer>
    </>
  )
}
